import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAppContext } from '@/hooks/useAppContext';
import { useScryfallStatusContext } from '@/hooks/useScryfallStatusContext';
import { ScryfallClient } from '@/lib/scryfall';

/**
 * Hook that returns a configured ScryfallClient instance
 */
export function useScryfall() {
  const { config } = useAppContext();
  const { setApiDown } = useScryfallStatusContext();

  const client = useMemo(() => {
    return new ScryfallClient(config.scryfallApiUrl, () => {
      setApiDown(true);
    });
  }, [config.scryfallApiUrl, setApiDown]);

  return client;
}

/**
 * Hook for searching cards with infinite scroll support
 */
import { ScryfallSearchResult } from '@/lib/scryfall';

export function useScryfallSearch(query: string, sortBy: string, sortOrder: string, initialData?: ScryfallSearchResult, enabled = true) {
  const scryfall = useScryfall();

  return useInfiniteQuery({
    queryKey: ['scryfall-search', query, sortBy, sortOrder],
    queryFn: async ({ signal, pageParam = 1 }) => {
      return scryfall.searchCards({
        query,
        sortBy,
        sortOrder,
        page: pageParam,
        signal,
      });
    },
    initialData: initialData ? { pages: [initialData], pageParams: [1] } : undefined,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next_page,
    enabled: enabled && query.trim().length > 0 && !initialData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for fetching a specific card by ID
 */
export function useScryfallCard(cardId: string, enabled = true) {
  const scryfall = useScryfall();

  return useQuery({
    queryKey: ['scryfall-card', cardId],
    queryFn: async ({ signal }) => {
      return scryfall.getCard(cardId, signal);
    },
    enabled: enabled && !!cardId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook for fetching all printings of a card by oracle ID
 */
export function useScryfallPrintings(oracleId: string, enabled = true) {
  const scryfall = useScryfall();

  return useQuery({
    queryKey: ['scryfall-printings', oracleId],
    queryFn: async ({ signal }) => {
      return scryfall.getCardPrintings(oracleId, signal);
    },
    enabled: enabled && !!oracleId,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}